/**
 * Client instrumentation (Next.js) — runs once on the client before the app
 * becomes interactive, so anything here is in place before the first component
 * renders. See https://nextjs.org/docs/app/api-reference/file-conventions/instrumentation-client
 *
 * `crypto.randomUUID` polyfill for non-secure browsing contexts (LF-4605).
 *
 * `crypto.randomUUID` is a **secure-context-only** API: it exists on HTTPS and
 * `http://localhost`, but is `undefined` on arbitrary plain-HTTP origins — e.g.
 * a dev server reached via a custom hostname like `http://vale-damien:3000`.
 * Several components call it in a `useState` initialiser (billing contacts,
 * invite-team form, distribution attachments…), so on such hosts they throw
 * `crypto.randomUUID is not a function` during render and the page-level error
 * boundary trips before anything downstream (including the billing tab) shows.
 *
 * `crypto.getRandomValues` IS available in insecure contexts, so we backfill an
 * RFC-4122 v4 generator from it (the same fallback TanStack DB adopted). This
 * must run before app code — hence `instrumentation-client`, not a component or
 * `useEffect`, both of which would execute too late.
 *
 * No-op in production and on localhost: those are secure contexts where
 * `crypto.randomUUID` already exists, so the guard never assigns.
 */
try {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID !== 'function'
  ) {
    crypto.randomUUID = function randomUUID() {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      // RFC 4122 §4.4: set the version (4) and variant (10xx) bits.
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
      const uuid = `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
        .slice(6, 8)
        .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
      return uuid as `${string}-${string}-${string}-${string}-${string}`;
    };
  }
} catch {
  // Defensive per Next.js guidance — instrumentation must never break app boot.
}
